<template>
    <div class="CLocationInput relative">
        <CFormInput
            ref="inputRef"
            v-model="searchQuery"
            :disabled="disabled"
            placeholder="Začni písať mesto..."
            autocomplete="off"
            @input="handleSearch"
            @focus="showDropdown = true"
            @blur="handleBlur"
            @keydown.down.prevent="navigateDown"
            @keydown.up.prevent="navigateUp"
            @keydown.enter.prevent="selectHighlighted"
            @keydown.escape="showDropdown = false"
        />

        <!-- Dropdown -->
        <div
            v-if="showDropdown && (filteredCities.length > 0 || isLoading)"
            class="absolute z-50 mt-1 max-h-48 w-full overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg dark:border-default dark:bg-surface-card"
        >
            <!-- Loading state -->
            <div v-if="isLoading" class="px-4 py-3 text-sm text-gray-500 dark:text-txt-body">
                Hľadám...
            </div>

            <!-- Results -->
            <div
                v-for="(city, index) in filteredCities"
                v-else
                :key="city.place_id"
                :class="[
                    'cursor-pointer px-4 py-2.5 text-sm',
                    highlightedIndex === index
                        ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                        : 'text-gray-700 hover:bg-gray-50 dark:text-txt-body dark:hover:bg-subtle/50'
                ]"
                @mousedown.prevent="selectCity(city)"
                @mouseenter="highlightedIndex = index"
            >
                {{ city.display_name }}
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'

interface NominatimResult {
    place_id: number
    display_name: string
    lat: string
    lon: string
    address: {
        city?: string
        town?: string
        village?: string
        municipality?: string
        state?: string
        country?: string
    }
}

interface LocationData {
    geoCity: string
    geoCountry: string
    geoRegion: string | null
    geoCoordinates?: { lat: number; lng: number } | null
}

interface Props {
    modelValue?: LocationData
    disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    disabled: false
})

const emit = defineEmits<{
    'update:modelValue': [value: LocationData]
}>()

// State
const inputRef = ref<any>(null)
const searchQuery = ref('')
const showDropdown = ref(false)
const highlightedIndex = ref(0)
const filteredCities = ref<NominatimResult[]>([])
const isLoading = ref(false)

// Debounce timer
let searchTimeout: ReturnType<typeof setTimeout> | null = null

// Search using Nominatim API
async function searchNominatim(query: string) {
    if (query.length < 2) {
        filteredCities.value = []
        return
    }

    isLoading.value = true

    try {
        const response = await $fetch<NominatimResult[]>(
            'https://nominatim.openstreetmap.org/search',
            {
                params: {
                    q: query,
                    format: 'json',
                    addressdetails: 1,
                    limit: 8,
                    featuretype: 'city'
                },
                headers: {
                    'Accept-Language': 'sk,en'
                }
            }
        )

        // Deduplicate results by display_name
        const seen = new Set<string>()
        filteredCities.value = response.filter(city => {
            if (seen.has(city.display_name)) {
                return false
            }
            seen.add(city.display_name)
            return true
        })
        highlightedIndex.value = 0
    } catch (error) {
        console.error('Nominatim search failed:', error)
        filteredCities.value = []
    } finally {
        isLoading.value = false
    }
}

function handleSearch() {
    // Debounce: wait 300ms after user stops typing
    if (searchTimeout) {
        clearTimeout(searchTimeout)
    }

    searchTimeout = setTimeout(() => {
        searchNominatim(searchQuery.value)
    }, 300)
}

function handleBlur() {
    // Delay to allow click on dropdown item
    setTimeout(() => {
        showDropdown.value = false
    }, 150)
}

function navigateDown() {
    if (highlightedIndex.value < filteredCities.value.length - 1) {
        highlightedIndex.value++
    }
}

function navigateUp() {
    if (highlightedIndex.value > 0) {
        highlightedIndex.value--
    }
}

function selectHighlighted() {
    if (filteredCities.value[highlightedIndex.value]) {
        selectCity(filteredCities.value[highlightedIndex.value])
    }
}

function selectCity(city: NominatimResult) {
    const address = city.address || {}

    // Extract city name (Nominatim uses different fields)
    const cityName = address.city || address.town || address.village || address.municipality || ''
    const region = address.state || ''
    const country = address.country || ''

    // Use the city name for display, fallback to first part of display_name
    const displayCity = cityName || city.display_name.split(',')[0].trim()

    searchQuery.value = displayCity
    showDropdown.value = false

    emit('update:modelValue', {
        geoCity: displayCity,
        geoCountry: country,
        geoRegion: region || null,
        geoCoordinates: {
            lat: parseFloat(city.lat),
            lng: parseFloat(city.lon)
        }
    })
}

// Initialize from modelValue
function initFromValue() {
    if (props.modelValue?.geoCity) {
        searchQuery.value = props.modelValue.geoCity
    }
}

watch(() => props.modelValue, initFromValue, { deep: true })

onMounted(() => {
    initFromValue()
})
</script>
